import React, { useState, useEffect } from 'react';
import SelectField from '../common/SelectField.js';
import { useNavigate, useParams } from 'react-router-dom';
import Header from '../common/Header.js';
import Sidebar from '../common/Sidebar.js';
import Alert from '../common/Alert.js';
import inventorySKUService from '../../services/InventorySKUService.js';
import inventoryService from '../../services/InventoryService.js';
import { Package } from "lucide-react";
import { useTranslation } from 'react-i18next';


const AddInventorySKU = () => {
  const { inventoryId } = useParams();
  const [inventoryItem, setInventoryItem] = useState(null);
  const [formData, setFormData] = useState({
    inventory_id: inventoryId || '',
    sku_code: '',
    sku_name: '',
    quantity: '',
    unit_price: '',
    variant_type: '',
    variant_value: '',
    bin_location: '',
    notes: '',
    is_active: 'true'
  });
  
  const [skuImage, setSkuImage] = useState(null);
  const [skuImagePreview, setSkuImagePreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadingInventory, setLoadingInventory] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    if (inventoryId) {
      fetchInventoryItem();
    } else {
      setLoadingInventory(false);
    }
  }, [inventoryId]);

  const fetchInventoryItem = async () => {
    try {
      const response = await inventoryService.getById(inventoryId);
      setInventoryItem(response.data);
    } catch (err) {
      Alert.error('Failed to load inventory item');
      console.error(err);
    } finally {
      setLoadingInventory(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSKUImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        Alert.error('SKU image must be less than 5MB');
        return;
      }
      setSkuImage(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setSkuImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const removeSKUImage = () => {
    setSkuImage(null);
    setSkuImagePreview(null);
    const fileInput = document.getElementById('sku-image-input');
    if (fileInput) fileInput.value = '';
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await inventorySKUService.create(formData, skuImage);
      Alert.success('SKU created successfully and inventory updated!');
      
      // Show inventory update info
      if (response.inventory_updated) {
        Alert.info(`Inventory updated: ${response.inventory_updated.total_quantity} ${inventoryItem?.unit || 'units'} total`);
      }
      
      setTimeout(() => {
        if (inventoryId) {
          navigate(`/inventory/view/${inventoryId}`);
        } else {
          navigate('/inventory');
        }
      }, 1500);
    } catch (err) {
      Alert.error(err.response?.data?.error || 'Failed to create SKU');
    } finally {
      setLoading(false);
    }
  };

  if (loadingInventory) {
    return (
      <div className="max-w-[1850px] mx-auto">
        <Header />
        <div className="flex">
          <Sidebar />
          <div className="flex bg-light w-full mt-16 sm:mt-0">
            <div className="flex-1 flex flex-col overflow-hidden">
              <main className="flex-1 flex items-center justify-center">
                <div className="text-center">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500 mx-auto mb-4"></div>
                  <p className="text-sm sm:text-base text-gray-600">Loading...</p>
                </div>
              </main>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-[1850px] mx-auto">
      <Header />
      <div className="flex">
        <Sidebar />
        <div className="flex bg-light w-full mt-16 sm:mt-0">
          <div className="flex-1 flex flex-col overflow-hidden">
            <main className="flex-1 overflow-auto p-3 sm:p-6">
              <div className="max-w-7xl mx-auto">
                {/* Header */}
                <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3 sm:gap-4 mb-4 sm:mb-4">
                  <h1 className="text-2xl sm:text-xl font-bold text-secondary flex items-center gap-2 sm:gap-3">
                    <Package className="w-5 h-5 text-buttonBg" /> Add New SKU
                  </h1>
                </div>

                {/* Parent Inventory Info */}
                {inventoryItem && (
                  <div className="bg-blue-50 border border-blue-200 rounded-sm p-4 mb-4">
                    <h3 className="text-sm font-semibold text-blue-900 mb-2">Parent Inventory Item</h3>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 text-sm">
                      <div>
                        <span className="text-blue-700">Item:</span>
                        <span className="ml-2 font-medium text-blue-900">{inventoryItem.item_name}</span>
                      </div>
                      <div>
                        <span className="text-blue-700">Category:</span>
                        <span className="ml-2 font-medium text-blue-900">{inventoryItem.category}</span>
                      </div>
                      <div>
                        <span className="text-blue-700">Current Stock:</span>
                        <span className="ml-2 font-medium text-blue-900">{inventoryItem.quantity} {inventoryItem.unit}</span>
                      </div>
                    </div>
                  </div>
                )}

                {/* Form */}
                <form onSubmit={handleSubmit} className="bg-white rounded-sm shadow-sm p-4 sm:p-8 space-y-6 sm:space-y-8">
                  {/* SKU Image Section */}
                  <div>
                    <h3 className="text-base sm:text-md font-semibold text-tertiary mb-3 sm:mb-4 pb-2 border-b-2 border-grey-400">SKU Image</h3>
                    <div className="flex flex-col sm:flex-row items-center sm:items-center gap-4 sm:gap-6">
                      <div className="w-20 h-20 sm:w-24 sm:h-24 border-2 border-gray-300 
                                      overflow-hidden bg-gray-100 flex items-center justify-center flex-shrink-0 rounded-md">
                        {skuImagePreview ? (
                          <img src={skuImagePreview} alt="SKU Preview" className="w-full h-full object-cover" />
                        ) : (
                          <Package className="w-10 h-10 text-gray-400" />
                        )}
                      </div>

                      <div className="flex-1 w-full">
                        <label className="block text-xs sm:text-sm font-medium text-gray-900 mb-1 sm:mb-2">
                          Upload SKU Image
                        </label>
                        <input
                          id="sku-image-input"
                          type="file"
                          accept="image/*"
                          onChange={handleSKUImageChange}
                          className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-sm 
                                    focus:outline-none focus:ring-2 focus:ring-blue-400 text-sm sm:text-base"
                        />
                        <p className="text-xs text-gray-600 mt-1">Accepted formats: JPG, PNG, GIF, WebP (Max 5MB)</p>

                        {skuImagePreview && (
                          <button
                            type="button"
                            onClick={removeSKUImage}
                            className="mt-2 text-xs sm:text-sm text-red-600 hover:text-red-700"
                          >
                            Remove Image
                          </button>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Section 1: Basic Information */}
                  <div>
                    <h3 className="text-base sm:text-md font-semibold text-tertiary mb-3 sm:mb-4 pb-2 border-b-2 border-grey-400">Basic Information</h3>
                    <div className="grid grid-cols-1 sm:grid-cols-4 gap-4 sm:gap-6">
                      <div>
                        <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">
                          SKU Code <span className="text-danger font-bold">*</span>
                        </label>
                        <input
                          type="text"
                          name="sku_code"
                          value={formData.sku_code}
                          onChange={handleChange}
                          placeholder="Enter SKU code (e.g., SKU-RED-L)"
                          className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-blue-400 text-sm sm:text-base"
                          required
                        />
                      </div>

                      <div>
                        <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">
                          SKU Name <span className="text-danger font-bold">*</span>
                        </label>
                        <input
                          type="text"
                          name="sku_name"
                          value={formData.sku_name}
                          onChange={handleChange}
                          placeholder="Enter SKU name"
                          className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-blue-400 text-sm sm:text-base"
                          required
                        />
                      </div>

                      <div>
                        <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">
                          Variant Type
                        </label>
                        <input
                          type="text"
                          name="variant_type"
                          value={formData.variant_type}
                          onChange={handleChange}
                          placeholder="e.g., Size, Color, Model"
                          className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-blue-400 text-sm sm:text-base"
                        />
                      </div>

                      <div>
                        <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">
                          Variant Value
                        </label>
                        <input
                          type="text"
                          name="variant_value"
                          value={formData.variant_value}
                          onChange={handleChange}
                          placeholder="e.g., Large, Red, V2"
                          className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-blue-400 text-sm sm:text-base"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Section 2: Quantity and Pricing */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
                    <div>
                      <h3 className="text-base sm:text-md font-semibold text-tertiary mb-3 sm:mb-4 pb-2 border-b-2 border-grey-400">Quantity and Pricing</h3>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
                        <div>
                          <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">
                            Quantity <span className="text-danger font-bold">*</span>
                          </label>
                          <input
                            type="number"
                            name="quantity"
                            value={formData.quantity}
                            onChange={handleChange}
                            placeholder="Enter quantity"
                            min="0"
                            className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-blue-400 text-sm sm:text-base"
                            required
                          />
                          <p className="text-xs text-gray-600 mt-1">This quantity will be added to parent inventory</p>
                        </div>

                        <div>
                          <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">
                            Unit Price (₹) <span className="text-danger font-bold">*</span>
                          </label>
                          <input
                            type="text"
                            name="unit_price"
                            value={formData.unit_price}
                            onChange={handleChange}
                            placeholder="Enter unit price"
                            min="0"
                            className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-blue-400 text-sm sm:text-base"
                            required
                          />
                          <p className="text-xs text-gray-600 mt-1">Parent inventory price will be recalculated</p>
                        </div>
                      </div>
                    </div>

                    {/* Section 3: Storage Location */}
                    <div>
                      <h3 className="text-base sm:text-md font-semibold text-tertiary mb-3 sm:mb-4 pb-2 border-b-2 border-grey-400">Storage Location</h3>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
                        <div>
                          <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">
                            Bin Location
                          </label>
                          <input
                            type="text"
                            name="bin_location"
                            value={formData.bin_location}
                            onChange={handleChange}
                            placeholder="e.g., Shelf A3, Bin 12"
                            className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-blue-400 text-sm sm:text-base"
                          />
                        </div>

                        <div>
                          <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">
                            Status
                          </label>
                          <SelectField variant="form"
                            name="is_active"
                            value={formData.is_active}
                            onChange={handleChange}
                            className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-blue-400 text-sm sm:text-base"
                          >
                            <option value="true">Active</option>
                            <option value="false">Inactive</option>
                          </SelectField>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Section 4: Additional Information */}
                  <div>
                    <h3 className="text-base sm:text-md font-semibold text-tertiary mb-3 sm:mb-4 pb-2 border-b-2 border-grey-400">Additional Information</h3>
                    <div className="grid grid-cols-1 gap-4 sm:gap-6">
                      <div>
                        <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">
                          Notes
                        </label>
                        <textarea
                          name="notes"
                          value={formData.notes}
                          onChange={handleChange}
                          placeholder="Enter any additional notes"
                          rows="1"
                          className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-blue-400 text-sm sm:text-base"
                        />
                      </div>
                    </div>
                  </div>

                  <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
                    <button
                      type="submit"
                      disabled={loading}
                      className="group flex items-center justify-center gap-2 px-3 sm:px-3 py-2.5 sm:py-2 text-xs sm:text-sm text-buttonTxt bg-buttonBg hover:text-buttonBg hover:bg-buttonTxt border 
border-current rounded-sm  transition-all duration-300 font-medium whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {loading ? 'Creating...' : 'Create SKU'}
                    </button>
                    <button
                      type="button"
                      onClick={() => inventoryId ? navigate(`/inventory/view/${inventoryId}`) : navigate('/inventory')}
                      className="group flex items-center justify-center gap-2 px-3 sm:px-3 py-2.5 sm:py-2 text-xs sm:text-sm text-buttonBg bg-buttonTxt hover:text-buttonTxt 
hover:bg-buttonBg border border-current rounded-sm  transition-all duration-300 font-medium whitespace-nowrap"
                    >
                      Cancel
                    </button>
                  </div>
                </form>
              </div>
            </main>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddInventorySKU;